import { redirect } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"

import { getSupabaseServerClient } from "@repo/supabase/server-client"
import { logger } from "@repo/utils/logger"
import { supabaseServerClientMiddleware } from "~/lib/middleware/supabase-server-client.middleware"
import { errorObject, isAbsoluteUrl, successObject } from "~/lib/utils/http"
import {
  signInWithPasswordSchema,
  signUpWithPasswordSchema,
  verifyOtpSchema,
} from "./auth.schemas"
import type { SsrSafeSession, SsrSafeUser } from "./auth.types"

const log = logger.child({
  module: "auth-functions",
})

export const signUpWithPasswordFn = createServerFn({ method: "POST" })
  .inputValidator(signUpWithPasswordSchema)
  .handler(async ({ data }) => {
    const supabase = getSupabaseServerClient()

    // Do not allow absolute URLs (like //evil.com/foo) in the next URL
    const nextUrl =
      data.next && !isAbsoluteUrl(data.next)
        ? encodeURIComponent(data.next)
        : "/"

    const { error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        captchaToken: data.captchaToken,
        emailRedirectTo: nextUrl,
        data: {
          name: data.name,
        },
      },
    })

    if (error) {
      log.error("Error signing up", { error })
      return errorObject("Error signing up")
    }

    throw redirect({
      to: "/auth/otp",
      search: {
        email: data.email,
        type: "email",
        next: data.next || "",
        token: "",
      },
    })
  })

export const verifyOtpFn = createServerFn({ method: "POST" })
  .inputValidator(verifyOtpSchema)
  .handler(async ({ data: { token, email, type, next } }) => {
    const supabase = getSupabaseServerClient()

    const { error } = await supabase.auth.verifyOtp({
      token,
      email,
      type,
    })

    if (error) {
      log.error("Error verifying OTP", { error })
      let errorMessage = "Error verifying OTP"

      switch (error.code) {
        case "otp_expired":
          errorMessage =
            "The OTP has expired. Click the 'Resend OTP' link to request a new one."
          break
        case "otp_disabled":
          errorMessage =
            "The OTP has been disabled. Reach out to support to request a new one."
          break
        case "invalid_credentials":
          errorMessage =
            "The OTP is invalid. Click the 'Resend OTP' link to request a new one."
          break
        default:
          errorMessage = "Error verifying OTP"
      }

      return errorObject(errorMessage)
    }

    throw redirect({
      href: next || "/",
    })
  })

export const signOutFn = createServerFn()
  .middleware([supabaseServerClientMiddleware])
  .handler(async ({ context }) => {
    const { error } = await context.supabase.auth.signOut({ scope: "local" })

    if (error) {
      log.error("Error signing out", { error })
      return errorObject("Error signing out")
    }

    throw redirect({
      href: "/",
    })
  })

// Makes a request to the supabase auth API to fetch the user
export const fetchUserFn = createServerFn({ method: "GET" })
  .middleware([supabaseServerClientMiddleware])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase.auth.getUser()

    if (error) {
      log.error("Error fetching user", { error })
      return errorObject("Error fetching user")
    }

    return successObject(data.user as SsrSafeUser)
  })

// Reads the session information from the JWT (server-side)
// Trade-offs: This is much faster than fetching the user, but it uses potentially stale data (for the lifetime of the JWT)
// Do not use this where it is critical to have the most up-to-date user data (fine for general app navigation)
// We use it for basic route access control, but the main security work horse is the RLS policies on each table.
export const fetchSessionFn = createServerFn({ method: "GET" })
  .middleware([supabaseServerClientMiddleware])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase.auth.getSession()

    if (error) {
      log.error("Error fetching session", { error })
      return errorObject("Error fetching session")
    }

    return successObject(data.session as SsrSafeSession)
  })

export const signInWithPasswordFn = createServerFn({ method: "POST" })
  .inputValidator(signInWithPasswordSchema)
  .handler(async ({ data }) => {
    const supabase = getSupabaseServerClient()

    const { error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
      options: {
        captchaToken: data.captchaToken,
      },
    })

    if (error) {
      log.error("Error signing in", { error })
      return errorObject("Error signing in")
    }

    const nextUrl = data.next && !isAbsoluteUrl(data.next) ? data.next : "/"

    // Redirect to the prev page stored in the "next" search param
    throw redirect({
      href: nextUrl,
    })
  })
